// Helpers reutilizáveis para gerar blocos JSON-LD.
// Use direto em createFileRoute({ head: () => ({ scripts: [...] }) }).

const SITE = "https://kentro.com.br";

export type Crumb = { name: string; path: string };

/** Monta um BreadcrumbList a partir de uma lista de crumbs. O primeiro normalmente é "Início". */
export function breadcrumbLd(crumbs: Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.path.startsWith("http") ? c.path : `${SITE}${c.path}`,
    })),
  };
}

/** Bloco WebPage genérico para páginas que não têm um tipo mais específico. */
export function webPageLd({
  url,
  name,
  description,
  inLanguage = "pt-BR",
  primaryImage,
}: {
  url: string;
  name: string;
  description: string;
  inLanguage?: string;
  primaryImage?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": url,
    url,
    name,
    description,
    inLanguage,
    isPartOf: { "@type": "WebSite", "@id": `${SITE}/#website` },
    ...(primaryImage ? { primaryImageOfPage: { "@type": "ImageObject", url: primaryImage } } : {}),
  };
}

/** Bloco Service para landing pages de produto/serviço. */
export function serviceLd({
  url,
  name,
  description,
  serviceType,
  areaServed = "BR",
  inLanguage = "pt-BR",
}: {
  url: string;
  name: string;
  description: string;
  serviceType: string;
  areaServed?: string;
  inLanguage?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": url,
    url,
    name,
    description,
    serviceType,
    areaServed,
    inLanguage,
    provider: {
      "@type": "Organization",
      name: "Kentro Sistemas",
      url: SITE,
      logo: `${SITE}/og-image.png`,
    },
  };
}

/** ContactPage + ContactPoint para páginas de contato/canal de denúncias. */
export function contactPageLd({
  url,
  name,
  description,
  telephones = [],
  emails = [],
  contactType = "customer support",
  inLanguage = "pt-BR",
}: {
  url: string;
  name: string;
  description: string;
  telephones?: string[];
  emails?: string[];
  contactType?: string;
  inLanguage?: string;
}) {
  const contactPoint = [
    ...telephones.map((t) => ({
      "@type": "ContactPoint",
      telephone: t,
      contactType,
      areaServed: "BR",
      availableLanguage: ["Portuguese", "English", "Spanish"],
    })),
    ...emails.map((e) => ({
      "@type": "ContactPoint",
      email: e,
      contactType,
      areaServed: "BR",
      availableLanguage: ["Portuguese", "English", "Spanish"],
    })),
  ];
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": url,
    url,
    name,
    description,
    inLanguage,
    mainEntity: {
      "@type": "Organization",
      name: "Kentro Sistemas",
      url: SITE,
      ...(contactPoint.length ? { contactPoint } : {}),
    },
  };
}

/** FAQPage para páginas com perguntas frequentes. */
export function faqLd(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** Helper para empacotar como script TanStack head(). */
export function ldScripts(...objs: unknown[]) {
  return objs.map((o) => ({
    type: "application/ld+json",
    children: JSON.stringify(o),
  }));
}
